/**
 * NOTES-V2 N4 — grupo colapsável (temporal ou por tipo).
 *
 * Análogo ao TaskGroup do TASKS-V2. Aceita label e cor como props
 * pra suportar tanto modo "date" (cores fixas Tailwind) quanto
 * "type" (cor hex dinâmica do NOTE_TYPES).
 *
 * Persistência: localStorage por `groupKey` + `mode` (chaves
 * distintas pra date e type não conflitarem).
 *
 * A11y: header é <button> com aria-expanded/aria-controls.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import trace from "@/services/executionTrace";

const STORAGE_KEY_PREFIX = "nexo_notes_group_collapsed_";

function _key(mode, groupKey) {
  return `${STORAGE_KEY_PREFIX}${mode}_${groupKey}`;
}

function _readPersisted(mode, groupKey, defaultCollapsed) {
  try {
    const raw = localStorage.getItem(_key(mode, groupKey));
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch {/* private mode */}
  return defaultCollapsed;
}

function _writePersisted(mode, groupKey, collapsed) {
  try {
    localStorage.setItem(_key(mode, groupKey), String(collapsed));
  } catch {/* */}
}

export default function NoteGroup({
  mode,            // "date" | "type"
  groupKey,        // e.g. "today" | "idea"
  label,
  count,
  colorHex = null, // se presente, override do dot pra cor hex inline
  dotCls = "bg-slate-500",
  textCls = "text-slate-300",
  countCls = "text-slate-400",
  defaultCollapsed = false,
  children,
}) {
  const [collapsed, setCollapsed] = useState(() =>
    _readPersisted(mode, groupKey, defaultCollapsed),
  );

  useEffect(() => {
    _writePersisted(mode, groupKey, collapsed);
  }, [mode, groupKey, collapsed]);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        trace.record("notes.group.toggle", {
          mode,
          group: groupKey,
          expanded: !next,
        });
      } catch {/* */}
      return next;
    });
  }, [mode, groupKey]);

  const listId = `note-group-${mode}-${groupKey}`;

  return (
    <div className="mb-4" data-testid={`note-group-${mode}-${groupKey}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        aria-controls={listId}
        className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/[0.02] transition-colors text-left"
      >
        <span
          className={`inline-block w-2 h-2 rounded-full ${colorHex ? "" : dotCls}`}
          style={colorHex ? { backgroundColor: colorHex } : undefined}
          aria-hidden="true"
        />
        <span className={`text-sm font-medium ${textCls}`}>{label}</span>
        <span className={`text-xs ${countCls}`}>({count})</span>
        <ChevronRight
          className={`w-4 h-4 text-slate-500 ml-auto transition-transform ${
            collapsed ? "" : "rotate-90"
          }`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            id={listId}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-1"
              data-testid={`note-group-list-${mode}-${groupKey}`}
            >
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
